import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const values = [
  { icon: 'ri-eye-line', titleKey: 'values.v1.title', descKey: 'values.v1.desc' },
  { icon: 'ri-shield-check-line', titleKey: 'values.v2.title', descKey: 'values.v2.desc' },
  { icon: 'ri-hand-heart-line', titleKey: 'values.v3.title', descKey: 'values.v3.desc' },
  { icon: 'ri-lightbulb-flash-line', titleKey: 'values.v4.title', descKey: 'values.v4.desc' },
  { icon: 'ri-global-line', titleKey: 'values.v5.title', descKey: 'values.v5.desc' },
  { icon: 'ri-leaf-line', titleKey: 'values.v6.title', descKey: 'values.v6.desc' },
];

export default function ValuesSection() {
  const { t } = useTranslation('about');
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="w-full bg-white py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-xs font-semibold text-warm-500 uppercase tracking-[0.2em] mb-3 block">
              {t('values.label')}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-700 mb-4">
              {t('values.title')}
            </h2>
            <p className="text-sm md:text-base text-ink-400 max-w-2xl mx-auto leading-relaxed">
              {t('values.subtitle')}
            </p>
            <div className="w-16 h-1 bg-warm-500 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {values.map((item, index) => (
              <div
                key={item.titleKey}
                className={`p-6 bg-white border border-ink-100 rounded-xl transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-warm-50 flex items-center justify-center">
                    <i className={`${item.icon} text-warm-500 text-xl`} />
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-ink-700">
                    {t(item.titleKey)}
                  </h3>
                </div>
                <p className="text-xs md:text-sm text-ink-400 leading-relaxed">
                  {t(item.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}